import { SliceProps } from "@/Slice/index"
import { JCRichText } from "@/components"
import { Favorite } from "@/components/Favorite"
import { usePromotionTag } from "@/lib/preFetch/prefetchPromoTag"
import {
    FeaturedValidProduct,
    Link,
    NextImage,
    ProductCarousel,
    ProductsContainer,
    PromotionalImage,
    getPdpPrice,
    toFeaturedValidProduct,
    transMoney,
    useFeaturedProducts,
    useI18n,
} from "@jog/react-components"
import { getGtmIdFromCT, imageSizes, typedBoolean } from "@jog/shared"
import { asLink, asText, isFilled } from "@prismicio/helpers"
import { ColorField, ImageField, LinkField, SelectField } from "@prismicio/types"
import classNames from "classnames"
import { useMemo } from "react"

const ProductItem = ({
    product,
    className,
    backgroundColor,
    displayNumber,
    lcp,
}: {
    product: FeaturedValidProduct
    backgroundColor?: ColorField
    className?: string
    displayNumber: number
    lcp: boolean
}) => {
    const promotionTag = usePromotionTag(product.promotionalBanner)
    const { currentPrice, originPrice, off } = getPdpPrice(product)
    const { $t } = useI18n()
    return (
        <div className={className}>
            <div className="h-full bg-white" style={{ backgroundColor }}>
                <div className="relative inline-block h-full w-full text-gray-base hover:text-gray-base hover:no-underline">
                    <div className="relative flex h-full flex-col">
                        <div className="imageBlock group relative">
                            <NextImage
                                alt={product.cover.alt}
                                height={500}
                                width={330}
                                className="img-fluid h-full w-full object-cover"
                                src={product.cover.url}
                                sizes={imageSizes({
                                    lg: { 4: "25vw", 5: "20vw", 6: "16vw" }[displayNumber],
                                    sm: "50vw",
                                })}
                                lcp={lcp}
                            />
                            <PromotionalImage
                                className="absolute -top-1.25 -right-1.25 w-17.5"
                                promotionTag={promotionTag}
                            />
                            <Favorite
                                productId={product.id}
                                name={product.name}
                                price={+(product.firstPrice || product.price)}
                                gtmId={getGtmIdFromCT(product.attributes)}
                            />
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col justify-between">
                            <span className="mt-2.75 mb-1 flex-1 py-0 px-4 text-base uppercase">{product.name}</span>
                            <span className="mb-1.5 flex flex-col gap-2 py-0 px-4">
                                {off > 0 ? (
                                    <>
                                        <span className="mt-2 font-primary text-13 leading-none text-gray-base">
                                            <span>{`${$t(`old price`)}: `}</span>
                                            <span className="line-through">{transMoney(originPrice)}</span>
                                        </span>
                                        <span className="font-primary text-lg font-bold leading-none text-red-main">
                                            {transMoney(currentPrice)}
                                        </span>
                                    </>
                                ) : (
                                    <span className="font-primary text-lg font-bold leading-none text-gray-base">
                                        {transMoney(currentPrice)}
                                    </span>
                                )}
                            </span>
                        </div>
                    </div>
                    <Link
                        href={product.path}
                        target={product.target ?? "_self"}
                        className="absolute inset-0"
                        aria-label={product.name}
                    />
                </div>
            </div>
        </div>
    )
}

const gridMap = {
    4: "lg:grid-cols-4",
    5: "lg:grid-cols-5",
    6: "lg:grid-cols-6",
}
export const useValidProducts = (items: { product_link: LinkField; cover: ImageField }[]) => {
    const { products } = useFeaturedProducts()
    return useMemo<FeaturedValidProduct[]>(
        () =>
            items
                .filter((i) => isFilled.link(i.product_link))
                .map(({ product_link, cover }) => {
                    const product = products.find((product) => asLink(product_link)?.includes(product.path))
                    if (!product) return
                    return toFeaturedValidProduct({ product_link, cover }, product)
                })
                .filter(typedBoolean),
        [items, products]
    )
}
const Carousel = ({
    items,
    primary,
    lcp,
}: {
    items: FeaturedValidProduct[]
    primary: { display_number: SelectField<"4" | "5" | "6">; background_color: ColorField }
    lcp: boolean
}) => (
    <ProductCarousel
        CarouselContainer={({ children, className }) => (
            <div className={classNames(className, "-ml-3 pt-1.25")}>{children}</div>
        )}
        items={items.slice(0, 20)}
        displayNumber={+primary.display_number}
        CarouselItem={({ item, className }) => (
            <ProductItem
                product={item}
                className={classNames(className, "pl-3")}
                backgroundColor={primary.background_color}
                displayNumber={+primary.display_number}
                lcp={lcp}
            />
        )}
        Button={({ className, ...props }) => (
            <button {...props} className={classNames(className, "hover:text-green-dark")} />
        )}
    />
)
export const FeaturedProducts = ({
    slice: { primary, items },
    className,
    lcp,
}: SliceProps<"featured_products"> & { lcp: boolean }) => {
    const validProduct = useValidProducts(items)
    if (!validProduct.length) return <></>
    return (
        <ProductsContainer
            className={className}
            backgroundColor={primary.background_color}
            descriptionColor={primary.description_color}
            titleColor={primary.title_color}
            alignment={primary.alignment}
            title={asText(primary.title)}
            description={primary.description}>
            {primary.carousel ? (
                <Carousel items={validProduct.slice(0, 20)} primary={primary} lcp={lcp} />
            ) : (
                <div
                    className={classNames(
                        "grid grid-cols-2 gap-3 py-3",
                        gridMap[primary.display_number] || gridMap["6"]
                    )}>
                    {validProduct.slice(0, +primary.display_number || 6).map((product, i) => (
                        <ProductItem
                            backgroundColor={primary.background_color}
                            product={product}
                            key={i}
                            displayNumber={+primary.display_number}
                            lcp={lcp}
                        />
                    ))}
                </div>
            )}
        </ProductsContainer>
    )
}

export const CarouselProducts = ({
    slice: { primary, items },
    className,
    lcp,
}: SliceProps<"carousel_products"> & { lcp: boolean }) => {
    const max = +primary.maximum_amount || 20
    const maximumAmount = max < +primary.display_number ? 20 : max
    const validProduct = useValidProducts(items)
    if (!validProduct.length) return <></>
    return (
        <ProductsContainer
            className={className}
            backgroundColor={primary.background_color}
            descriptionColor={primary.description_color}
            titleColor={primary.title_color}
            alignment={primary.alignment}
            title={asText(primary.title)}
            description={isFilled.richText(primary.descriptions) ? <JCRichText field={primary.descriptions} /> : null}>
            <Carousel items={validProduct.slice(0, maximumAmount)} primary={primary} lcp={lcp} />
        </ProductsContainer>
    )
}
